import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useFocusEffect } from '@react-navigation/native';
import {
  View,
  Text,
  TouchableOpacity,
  StyleSheet,
  ScrollView,
  Platform,
  Modal,
  Dimensions,
} from 'react-native';
import { StatusBar } from 'expo-status-bar';
import { useTheme } from '../context/ThemeContext';
import HamburgerMenu from '../components/HamburgerMenu';
import ProfileIcon from '../components/ProfileIcon';
import Stopwatch from '../components/Stopwatch';
import { getApiUrl } from '../utils/apiConfig';
import { dedupeCoursesForDropdown } from '../utils/coursesCatalog';
import UserBreakVideoPlayer from '../components/UserBreakVideoPlayer';
import { hasUserBreakVideos } from '../utils/userBreakVideoLinks';
import { hasSuperUserAccess } from '../utils/sessionUser';

const { width } = Dimensions.get('window');

// Grade order mapping
const GRADE_ORDER = {
  'Beginner': 0,
  'Yellow Belt': 1,
  'Green Belt': 2,
  'Red Belt': 3,
  'Brown Belt': 4,
  'Shodan Ho': 5,
  '1st Degree Black Belt': 6,
  '2nd Degree Black Belt': 7,
  '3rd Degree Black Belt': 8,
  '4th Degree Black Belt': 9,
  '5th Degree Black Belt': 10,
  '6th Degree Black Belt': 11,
  '7th Degree Black Belt': 12,
  '8th Degree Black Belt': 13,
  '9th Degree Black Belt': 14,
  '10th Degree Black Belt': 15,
};

const GRADE_NAMES = Object.keys(GRADE_ORDER);

const PREVIOUS_GRADES_OMIT = new Set(['Beginner']);

function getNextGrade(currentGrade) {
  const currentIndex = GRADE_NAMES.indexOf(currentGrade);
  if (currentIndex === -1 || currentIndex === GRADE_NAMES.length - 1) {
    return null; // No next grade
  }
  return GRADE_NAMES[currentIndex + 1];
}

function getYouTubeTitle(breakItem) {
  if (!breakItem) return '';
  
  const parts = [
    breakItem.break_name,
    breakItem.side,
    breakItem.material_type,
    breakItem.part_of_body,
    breakItem.weapon
  ].filter(Boolean);
  
  return parts.join(' - ') || 'Break Video';
}

/** Postgres `text[]` is usually an array in JSON; guard string/legacy shapes */
function normalizeCourseNames(raw) {
  if (raw == null) return [];
  if (Array.isArray(raw)) return raw.filter(Boolean).map(String);
  if (typeof raw === 'string') {
    try {
      const p = JSON.parse(raw);
      return Array.isArray(p) ? p.filter(Boolean).map(String) : [];
    } catch {
      return [];
    }
  }
  return [];
}

/** Display size for breaks: whole inches only (no decimals), with trailing ". */
function formatSizeWithInchQuote(raw) {
  if (raw === '' || raw == null) return '';
  const trimmed = String(raw).trim();
  if (trimmed === '') return '';

  const hasQuote = /["″]/.test(trimmed);
  const inner = hasQuote
    ? trimmed.replace(/["″]+\s*$/, '').trim()
    : trimmed;

  const twoPart = /^(\d+(?:\.\d+)?)\s*[x×]\s*(\d+(?:\.\d+)?)$/i.exec(inner);
  if (twoPart) {
    const a = Math.round(Number(twoPart[1]));
    const b = Math.round(Number(twoPart[2]));
    return `${a}x${b}"`;
  }

  const compact = inner.replace(/\s/g, '');
  const n = Number(compact);
  if (Number.isFinite(n) && /^-?[\d.]+$/.test(compact)) {
    return `${Math.round(n)}"`;
  }

  if (hasQuote) return trimmed;
  return `${trimmed}"`;
}

/** Side — weapon — quantity x size/material phrase (My Breaks detail line). */
function formatBreakDetailLine(b) {
  const side = b?.side && b.side !== 'N/A' ? b.side : '';
  const weapon = b?.weapon || '';
  const qtyRaw = b?.quantity != null && b.quantity !== '' ? Number(b.quantity) : 1;
  const qty = Number.isFinite(qtyRaw) && qtyRaw > 0 ? qtyRaw : 1;
  const mat = String(b?.material_type || '').toLowerCase();

  const sizeInchesRaw =
    b?.size_inches != null && b.size_inches !== ''
      ? String(b.size_inches).trim()
      : b?.total_size_inches != null && b.total_size_inches !== ''
        ? String(b.total_size_inches).trim()
        : b?.height != null && b.height !== ''
          ? String(b.height).trim()
          : '';

  const sizeWithQuote = formatSizeWithInchQuote(sizeInchesRaw);

  let detail = '';
  const atypicalVibratingPalm =
    /vibrating palm/i.test(String(b?.break_name || '')) &&
    (b?.size_inches == null || String(b.size_inches).trim() === '');

  if (mat.includes('brick')) {
    const w = qty > 1 ? 'bricks' : 'brick';
    detail = `${qty} x ${w}`;
  } else if (mat.includes('block')) {
    const w = qty > 1 ? 'blocks' : 'block';
    if (atypicalVibratingPalm && sizeWithQuote) {
      detail = `${sizeWithQuote} blocks`;
    } else {
      detail = sizeWithQuote
        ? `${qty} x ${sizeWithQuote} ${w}`
        : `${qty} x ${w}`;
    }
  } else if (mat.includes('wood')) {
    detail = sizeWithQuote
      ? `${qty} x ${sizeWithQuote} wood`
      : `${qty} x wood`;
  } else if (mat.includes('tile')) {
    const w = qty > 1 ? 'tiles' : 'tile';
    detail = `${qty} x ${w}`;
  } else {
    const base = [b?.material_type, sizeWithQuote].filter(Boolean).join(' ');
    detail = base || `${qty} x`;
  }

  return [side, weapon, detail].filter(Boolean).join(' - ');
}

export default function MyBreaksPage({ navigation, route }) {
  const theme = useTheme();
  const [currentUser, setCurrentUser] = useState(null);
  const [loadingUser, setLoadingUser] = useState(true);
  const stopwatchRef = useRef(null);
  const [activeTab, setActiveTab] = useState('nextGrade');
  
  // User and grade info
  const [nextGrade, setNextGrade] = useState(null);
  const [loadingGrade, setLoadingGrade] = useState(true);
  
  // Next Grade Breaks tab state
  const [nextGradeCompletedBreaks, setNextGradeCompletedBreaks] = useState([]);
  const [nextGradeTodoBreaks, setNextGradeTodoBreaks] = useState([]);
  const [loadingNextGrade, setLoadingNextGrade] = useState(false);

  const [gradesCatalog, setGradesCatalog] = useState([]);
  const [selectedPreviousGrade, setSelectedPreviousGrade] = useState(null);
  const [showPreviousGradeDropdown, setShowPreviousGradeDropdown] = useState(false);
  const [previousGradeCompletedBreaks, setPreviousGradeCompletedBreaks] = useState([]);
  const [previousGradeTodoBreaks, setPreviousGradeTodoBreaks] = useState([]);
  const [loadingPreviousGradeTab, setLoadingPreviousGradeTab] = useState(false);

  const [courses, setCourses] = useState([]);
  const [selectedCourseIds, setSelectedCourseIds] = useState([]);
  const [showCourseFilterModal, setShowCourseFilterModal] = useState(false);
  const [courseFilterDraft, setCourseFilterDraft] = useState([]);
  const [myBreaksFeedRows, setMyBreaksFeedRows] = useState([]);
  const [loadingMyBreaksFeed, setLoadingMyBreaksFeed] = useState(false);

  const [showBreakModal, setShowBreakModal] = useState(false);
  const [selectedBreak, setSelectedBreak] = useState(null);

  useFocusEffect(
    useCallback(() => {
      let cancelled = false;
      const partial = route?.params?.user;
      const uid = route?.params?.userId ?? route?.params?.user?.id;

      const apply = (u) => {
        if (!cancelled) {
          setCurrentUser(u);
          setLoadingUser(false);
        }
      };

      if (!uid) {
        apply(partial || null);
        return () => {
          cancelled = true;
        };
      }

      setLoadingUser(true);
      fetch(`${getApiUrl()}/api/users/${uid}`)
        .then((r) => r.json())
        .then((data) => {
          if (data?.error || !data?.id) {
            throw new Error(data?.error || 'User not found');
          }
          apply(data);
        })
        .catch((err) => {
          console.error('MyBreaksPage: could not load user from API', err);
          apply(partial || null);
        });

      return () => {
        cancelled = true;
      };
    }, [route?.params?.userId, route?.params?.user?.id])
  );
  
  useEffect(() => {
    if (currentUser) {
      fetchNextGrade();
      fetchCourses();
      fetch(`${getApiUrl()}/api/grades`)
        .then((r) => (r.ok ? r.json() : []))
        .then((data) => {
          if (Array.isArray(data)) setGradesCatalog(data);
        })
        .catch(() => setGradesCatalog([]));
    }
  }, [currentUser]);
  
  useEffect(() => {
    if (nextGrade && currentUser) {
      fetchNextGradeBreaks();
    }
  }, [nextGrade, currentUser]);
  
  const fetchNextGrade = async () => {
    if (!currentUser) return;
    
    setLoadingGrade(true);
    try {
      // Fetch grades to find next grade
      const gradesResponse = await fetch(`${getApiUrl()}/api/grades`);
      if (gradesResponse.ok) {
        const grades = await gradesResponse.json();
        const currentGrade = grades.find(g => g.grade_name === currentUser.student_grade);
        
        if (currentGrade) {
          const nextGradeData = grades.find(g => g.grade_rank_order === currentGrade.grade_rank_order + 1);
          if (nextGradeData) {
            setNextGrade(nextGradeData.grade_name);
          }
        } else {
          // Fallback to hardcoded logic if grade not found in table
          const next = getNextGrade(currentUser.student_grade);
          setNextGrade(next);
        }
      }
    } catch (error) {
      console.error('Error fetching next grade:', error);
      // Fallback to hardcoded logic
      const next = getNextGrade(currentUser.student_grade);
      setNextGrade(next);
    } finally {
      setLoadingGrade(false);
    }
  };
  
  const previousGradeOptions = useMemo(() => {
    if (!currentUser || !gradesCatalog.length) return [];
    const cg = gradesCatalog.find((g) => g.grade_name === currentUser.student_grade);
    const rank =
      cg != null && cg.grade_rank_order != null ? Number(cg.grade_rank_order) : -1;
    return gradesCatalog
      .filter(
        (g) =>
          g.grade_name &&
          !PREVIOUS_GRADES_OMIT.has(g.grade_name) &&
          g.grade_rank_order != null &&
          Number(g.grade_rank_order) <= rank
      )
      .sort((a, b) => Number(b.grade_rank_order) - Number(a.grade_rank_order));
  }, [currentUser, gradesCatalog]);

  useEffect(() => {
    if (!previousGradeOptions.length) {
      setSelectedPreviousGrade(null);
      return;
    }
    setSelectedPreviousGrade((prev) => {
      if (prev && previousGradeOptions.some((g) => g.grade_name === prev)) return prev;
      return previousGradeOptions[0].grade_name;
    });
  }, [previousGradeOptions]);

  const fetchMyBreaksFeed = useCallback(async () => {
    if (!currentUser) return;
    setLoadingMyBreaksFeed(true);
    try {
      const params = new URLSearchParams();
      if (selectedCourseIds.length > 0) {
        params.set('course_ids', selectedCourseIds.join(','));
        params.set('limit', '100');
      } else {
        params.set('limit', '5');
      }
      const url = `${getApiUrl()}/api/students/${currentUser.id}/my-breaks-feed?${params}`;
      const response = await fetch(url);
      if (response.ok) {
        const data = await response.json();
        setMyBreaksFeedRows(Array.isArray(data) ? data : []);
      } else {
        setMyBreaksFeedRows([]);
      }
    } catch (e) {
      console.error('Error fetching my breaks feed:', e);
      setMyBreaksFeedRows([]);
    } finally {
      setLoadingMyBreaksFeed(false);
    }
  }, [currentUser, selectedCourseIds]);

  useEffect(() => {
    if (activeTab !== 'courses' || !currentUser) return;
    fetchMyBreaksFeed();
  }, [activeTab, currentUser, fetchMyBreaksFeed]);
  
  const fetchNextGradeBreaks = async () => {
    if (!currentUser || !nextGrade) return;
    
    setLoadingNextGrade(true);
    try {
      // Fetch completed breaks
      const completedResponse = await fetch(
        `${getApiUrl()}/api/breaks/grade/${encodeURIComponent(nextGrade)}/student/${currentUser.id}?status=completed`
      );
      if (completedResponse.ok) {
        const completed = await completedResponse.json();
        setNextGradeCompletedBreaks(completed);
      }
      
      // Fetch todo breaks
      const todoResponse = await fetch(
        `${getApiUrl()}/api/breaks/grade/${encodeURIComponent(nextGrade)}/student/${currentUser.id}?status=not_completed`
      );
      if (todoResponse.ok) {
        const todo = await todoResponse.json();
        setNextGradeTodoBreaks(todo);
      }
    } catch (error) {
      console.error('Error fetching next grade breaks:', error);
    } finally {
      setLoadingNextGrade(false);
    }
  };
  
  const fetchPreviousGradeTabBreaks = async () => {
    if (!currentUser || !selectedPreviousGrade) return;

    setLoadingPreviousGradeTab(true);
    try {
      const [completedResponse, todoResponse] = await Promise.all([
        fetch(
          `${getApiUrl()}/api/breaks/grade/${encodeURIComponent(selectedPreviousGrade)}/student/${currentUser.id}?status=completed`
        ),
        fetch(
          `${getApiUrl()}/api/breaks/grade/${encodeURIComponent(selectedPreviousGrade)}/student/${currentUser.id}?status=not_completed`
        ),
      ]);
      if (completedResponse.ok) {
        setPreviousGradeCompletedBreaks(await completedResponse.json());
      } else {
        setPreviousGradeCompletedBreaks([]);
      }
      if (todoResponse.ok) {
        setPreviousGradeTodoBreaks(await todoResponse.json());
      } else {
        setPreviousGradeTodoBreaks([]);
      }
    } catch (error) {
      console.error('Error fetching previous grade breaks:', error);
    } finally {
      setLoadingPreviousGradeTab(false);
    }
  };

  useEffect(() => {
    if (activeTab !== 'previous' || !currentUser || !selectedPreviousGrade)
      return;
    fetchPreviousGradeTabBreaks();
  }, [activeTab, currentUser, selectedPreviousGrade]);

  const fetchCourses = async () => {
    if (!currentUser) {
      setCourses([]);
      return;
    }

    const enrolledNames = normalizeCourseNames(currentUser.courses);
    const superUserAll =
      hasSuperUserAccess(currentUser) && enrolledNames.length === 0;

    if (!superUserAll && enrolledNames.length === 0) {
      setCourses([]);
      return;
    }
    
    try {
      const response = await fetch(`${getApiUrl()}/api/courses`);
      if (response.ok) {
        const raw = await response.json();
        const allCourses = dedupeCoursesForDropdown(Array.isArray(raw) ? raw : []);
        const enrolledCourses = superUserAll
          ? allCourses
          : allCourses.filter((course) =>
              enrolledNames.includes(course.course_name)
            );
        setCourses(enrolledCourses);
      }
    } catch (error) {
      console.error('Error fetching courses:', error);
    }
  };

  const styles = getStyles(theme);
  const tabAccent = theme.colors.primary || '#d4af37';

  const handleBreakPress = (breakItem) => {
    setSelectedBreak(breakItem);
    setShowBreakModal(true);
  };

  const statusChipKey = (status) => {
    if (status === 'completed') return 'completed';
    if (status === 'in progress') return 'in-progress';
    if (status === 'attempted') return 'attempted';
    return 'not-started';
  };

  const renderStatusBadge = (item) => {
    const status = item.status || 'not started';
    const key = statusChipKey(item.status);
    return (
      <View
        style={[
          styles.statusBadge,
          key === 'completed' && styles.statuscompleted,
          key === 'in-progress' && styles.statusinprogress,
          key === 'attempted' && styles.statusattempted,
          key === 'not-started' && styles.statusnotstarted,
        ]}
        {...(Platform.OS === 'web'
          ? { dataSet: { myBreaksStatusBadge: 'true', myBreaksStatus: key } }
          : {})}
      >
        <Text style={styles.statusBadgeText}>{status}</Text>
      </View>
    );
  };

  const renderBreakRow = ({ item }) => (
    <TouchableOpacity
      style={styles.breakRow}
      onPress={() => handleBreakPress(item)}
    >
      <Text style={styles.breakName}>{item.break_name}</Text>
      <Text style={styles.breakDetails}>{formatBreakDetailLine(item)}</Text>
      {renderStatusBadge(item)}
    </TouchableOpacity>
  );

  const renderCourseBreakRow = ({ item }) => {
    const courseLabel =
      item.course_names && String(item.course_names).trim()
        ? String(item.course_names).trim()
        : null;
    return (
      <TouchableOpacity
        style={styles.breakRow}
        onPress={() => handleBreakPress(item)}
      >
        <View
          style={styles.breakRowTitleRow}
          nativeID={`my-breaks-title-${item.user_break_id ?? item.break_id}`}
          data-my-breaks-title-row="true"
        >
          <Text style={[styles.breakName, styles.breakNameInRow]} numberOfLines={1}>
            {item.break_name}
          </Text>
          {courseLabel ? (
            <Text style={styles.breakRowCourseTag} numberOfLines={1}>
              {courseLabel}
            </Text>
          ) : null}
        </View>
        <Text style={styles.breakDetails}>{formatBreakDetailLine(item)}</Text>
        {renderStatusBadge(item)}
      </TouchableOpacity>
    );
  };

  const renderSectionPill = (label) => (
    <View
      style={[
        styles.sectionPill,
        { borderColor: tabAccent },
      ]}
    >
      <Text style={[styles.sectionPillText, { color: tabAccent }]}>{label}</Text>
    </View>
  );

  const renderMyBreaksFeedList = () => {
    if (loadingMyBreaksFeed) {
      return <Text style={styles.loadingText}>Loading...</Text>;
    }
    if (!myBreaksFeedRows.length) {
      return <Text style={styles.emptyText}>No breaks to show</Text>;
    }
    return (
      <View>
        {myBreaksFeedRows.map((item, index) => (
          <View key={`feed-${item.user_break_id || item.break_id}-${index}`}>
            {renderCourseBreakRow({ item })}
          </View>
        ))}
      </View>
    );
  };

  const renderBreakModal = () => {
    if (!selectedBreak) return null;
    
    const hasVideo = hasUserBreakVideos(selectedBreak?.video_link);
    
    return (
      <Modal
        visible={showBreakModal}
        transparent={true}
        animationType="slide"
        onRequestClose={() => setShowBreakModal(false)}
      >
        <View style={styles.modalOverlay}>
          <View style={styles.modalContent}>
            <TouchableOpacity
              style={styles.modalCloseButton}
              onPress={() => setShowBreakModal(false)}
            >
              <Text style={styles.modalCloseText}>✕</Text>
            </TouchableOpacity>
            
            <Text style={styles.modalTitle}>{selectedBreak.break_name}</Text>
            
            {hasVideo ? (
              <>
                <UserBreakVideoPlayer
                  videoLinks={selectedBreak.video_link}
                  title={selectedBreak.break_name || getYouTubeTitle(selectedBreak)}
                  theme={theme}
                />
                <Text style={styles.videoTitle}>
                  {selectedBreak.break_name || getYouTubeTitle(selectedBreak)}
                </Text>
              </>
            ) : (
              <View style={styles.noVideoContainer}>
                <Text style={styles.noVideoText}>
                  Break missing/not loaded into YouTube.
                </Text>
                <TouchableOpacity
                  style={styles.uploadButton}
                  onPress={() => {
                    setShowBreakModal(false);
                    navigation.navigate('BreaksBuilder', { user: currentUser });
                  }}
                >
                  <Text style={styles.uploadButtonText}>Do it here</Text>
                </TouchableOpacity>
              </View>
            )}
          </View>
        </View>
      </Modal>
    );
  };

  if (loadingUser || !currentUser) {
    return (
      <View style={styles.container}>
        <Text style={styles.loadingText}>Loading...</Text>
      </View>
    );
  }
  
  return (
    <View
      style={styles.container}
      data-screen="my-breaks"
      data-theme={theme.isDarkMode ? 'dark' : 'light'}
    >
      <StatusBar style={theme.isDarkMode ? 'light' : 'dark'} />
      
      {/* Header */}
      <View style={styles.header}>
        <View style={styles.hamburgerContainer}>
          <HamburgerMenu
            navigation={navigation}
            stopwatchRef={stopwatchRef}
            currentUser={currentUser}
          />
        </View>
        <View style={styles.breadcrumbContainer}>
          <Text style={styles.breadcrumb}>My Breaks</Text>
        </View>
        <View style={styles.profileIconContainer}>
          <ProfileIcon navigation={navigation} user={currentUser} />
        </View>
      </View>
      
      {/* Tabs */}
      <View style={styles.tabsContainer}>
        <TouchableOpacity
          style={[styles.tab, activeTab === 'nextGrade' && styles.activeTab]}
          onPress={() => setActiveTab('nextGrade')}
        >
          <Text style={[styles.tabText, activeTab === 'nextGrade' && styles.activeTabText]}>
            My next Grade breaks
          </Text>
        </TouchableOpacity>
        <TouchableOpacity
          style={[styles.tab, activeTab === 'previous' && styles.activeTab]}
          onPress={() => setActiveTab('previous')}
        >
          <Text style={[styles.tabText, activeTab === 'previous' && styles.activeTabText]}>
            Previous Grades Breaks
          </Text>
        </TouchableOpacity>
        <TouchableOpacity
          style={[styles.tab, activeTab === 'courses' && styles.activeTab]}
          onPress={() => setActiveTab('courses')}
        >
          <Text style={[styles.tabText, activeTab === 'courses' && styles.activeTabText]}>
            My Courses Breaks
          </Text>
        </TouchableOpacity>
      </View>
      
      {/* Tab Content */}
      <ScrollView style={styles.content} showsVerticalScrollIndicator={true}>
        {activeTab === 'nextGrade' && (
          <View style={styles.tabContent}>
            <Text style={styles.sectionTitle}>
              Relevant breaks - {nextGrade || 'N/A'}
            </Text>
            
            {/* Completed Breaks Section */}
            <View style={styles.section}>
              {renderSectionPill('Completed Breaks')}
              {loadingNextGrade ? (
                <Text style={styles.loadingText}>Loading...</Text>
              ) : nextGradeCompletedBreaks.length === 0 ? (
                <Text style={styles.emptyText}>No completed breaks</Text>
              ) : (
                <View>
                  {nextGradeCompletedBreaks.map((item) => (
                    <View key={`completed-${item.break_id}`}>
                      {renderBreakRow({ item })}
                    </View>
                  ))}
                </View>
              )}
            </View>
            
            {/* To Do Breaks Section */}
            <View style={styles.section}>
              {renderSectionPill('To do/Never Done Breaks')}
              {loadingNextGrade ? (
                <Text style={styles.loadingText}>Loading...</Text>
              ) : nextGradeTodoBreaks.length === 0 ? (
                <Text style={styles.emptyText}>No breaks to do</Text>
              ) : (
                <View>
                  {nextGradeTodoBreaks.map((item) => (
                    <View key={`todo-${item.break_id}`}>
                      {renderBreakRow({ item })}
                    </View>
                  ))}
                </View>
              )}
            </View>
          </View>
        )}
        
        {activeTab === 'previous' && (
          <View style={styles.tabContent}>
            <Text style={styles.sectionTitle}>
              Previous grades —{' '}
              {currentUser.student_name || currentUser.username}
            </Text>

            {previousGradeOptions.length === 0 ? (
              <Text style={styles.emptyText}>
                No grades to show for your current rank.
              </Text>
            ) : (
              <>
                <View style={[styles.dropdownContainer, { zIndex: 1000 }]}>
                  <TouchableOpacity
                    style={styles.dropdownButton}
                    onPress={() =>
                      setShowPreviousGradeDropdown(!showPreviousGradeDropdown)
                    }
                    {...(Platform.OS === 'web' ? { dataSet: { dropdownTrigger: 'true' } } : {})}
                  >
                    <Text style={styles.dropdownButtonText}>
                      Grade: {selectedPreviousGrade || 'Select grade'}
                      {selectedPreviousGrade === currentUser.student_grade
                        ? ' (current)'
                        : ''}
                    </Text>
                    <Text style={styles.dropdownArrow}>
                      {showPreviousGradeDropdown ? '▲' : '▼'}
                    </Text>
                  </TouchableOpacity>

                  {showPreviousGradeDropdown && (
                    <View
                      style={styles.dropdownMenu}
                      {...(Platform.OS === 'web' ? { dataSet: { dropdownSurface: 'true' } } : {})}
                    >
                      <ScrollView style={styles.dropdownScrollView}>
                        {previousGradeOptions.map((g) => (
                          <TouchableOpacity
                            key={g.grade_name}
                            style={styles.dropdownOption}
                            onPress={() => {
                              setSelectedPreviousGrade(g.grade_name);
                              setShowPreviousGradeDropdown(false);
                            }}
                            {...(Platform.OS === 'web' ? { dataSet: { dropdownRow: 'true' } } : {})}
                          >
                            <Text style={styles.dropdownOptionText}>
                              {g.grade_name}
                              {g.grade_name === currentUser.student_grade
                                ? ' (current)'
                                : ''}
                            </Text>
                          </TouchableOpacity>
                        ))}
                      </ScrollView>
                    </View>
                  )}
                </View>

                <View style={styles.section}>
                  {renderSectionPill('Completed Breaks')}
                  {loadingPreviousGradeTab ? (
                    <Text style={styles.loadingText}>Loading...</Text>
                  ) : previousGradeCompletedBreaks.length === 0 ? (
                    <Text style={styles.emptyText}>No completed breaks</Text>
                  ) : (
                    <View>
                      {previousGradeCompletedBreaks.map((item) => (
                        <View key={`prev-c-${item.break_id}`}>
                          {renderBreakRow({ item })}
                        </View>
                      ))}
                    </View>
                  )}
                </View>

                <View style={styles.section}>
                  {renderSectionPill('To do/Never Done Breaks')}
                  {loadingPreviousGradeTab ? (
                    <Text style={styles.loadingText}>Loading...</Text>
                  ) : previousGradeTodoBreaks.length === 0 ? (
                    <Text style={styles.emptyText}>No breaks to do</Text>
                  ) : (
                    <View>
                      {previousGradeTodoBreaks.map((item) => (
                        <View key={`prev-t-${item.break_id}`}>
                          {renderBreakRow({ item })}
                        </View>
                      ))}
                    </View>
                  )}
                </View>
              </>
            )}
          </View>
        )}
        
        {activeTab === 'courses' && (
          <View style={styles.tabContent}>
            <Text style={styles.sectionTitle}>
              My course breaks —{' '}
              {currentUser.student_name || currentUser.username}
            </Text>

            <TouchableOpacity
              style={[styles.filterCoursesButton, { borderColor: tabAccent }]}
              onPress={() => {
                setCourseFilterDraft([...selectedCourseIds]);
                setShowCourseFilterModal(true);
              }}
            >
              <Text
                style={[styles.filterCoursesButtonText, { color: tabAccent }]}
              >
                Filter by courses
                {selectedCourseIds.length > 0
                  ? ` (${selectedCourseIds.length})`
                  : ''}
              </Text>
            </TouchableOpacity>
            {selectedCourseIds.length > 0 ? (
              <TouchableOpacity
                onPress={() => setSelectedCourseIds([])}
                style={styles.clearFilterLink}
              >
                <Text style={styles.clearFilterText}>Clear course filter</Text>
              </TouchableOpacity>
            ) : null}

            <Text style={styles.subsectionTitle}>
              {selectedCourseIds.length === 0
                ? 'Recent breaks (last 5, by date recorded)'
                : 'Breaks for selected courses'}
            </Text>
            <View style={styles.courseBreaksListOuter}>
              {renderMyBreaksFeedList()}
            </View>

            <Modal
              visible={showCourseFilterModal}
              transparent
              animationType="fade"
              onRequestClose={() => setShowCourseFilterModal(false)}
            >
              <View style={styles.modalOverlay}>
                <View style={styles.modalContent}>
                  <TouchableOpacity
                    style={styles.modalCloseButton}
                    onPress={() => setShowCourseFilterModal(false)}
                  >
                    <Text style={styles.modalCloseText}>✕</Text>
                  </TouchableOpacity>
                  <Text style={styles.modalTitle}>Your courses</Text>
                  <Text style={styles.modalHint}>
                    Multi-select courses you are enrolled in. The list shows
                    your breaks tied to those courses through course breaks.
                  </Text>
                  <ScrollView style={styles.courseFilterScroll}>
                    {courses.length === 0 ? (
                      <Text style={styles.emptyText}>No courses available</Text>
                    ) : (
                      courses.map((c) => {
                        const on = courseFilterDraft.includes(c.id);
                        return (
                          <TouchableOpacity
                            key={c.id}
                            style={styles.checkboxRow}
                            onPress={() =>
                              setCourseFilterDraft((d) =>
                                on
                                  ? d.filter((x) => x !== c.id)
                                  : [...d, c.id]
                              )
                            }
                          >
                            <Text style={styles.checkboxMark}>
                              {on ? '☑' : '☐'}
                            </Text>
                            <Text style={styles.dropdownOptionText}>
                              {c.course_name}
                            </Text>
                          </TouchableOpacity>
                        );
                      })
                    )}
                  </ScrollView>
                  <View style={styles.modalActionsRow}>
                    <TouchableOpacity
                      style={styles.modalSecondaryButton}
                      onPress={() => setShowCourseFilterModal(false)}
                    >
                      <Text style={styles.modalSecondaryButtonText}>
                        Cancel
                      </Text>
                    </TouchableOpacity>
                    <TouchableOpacity
                      style={styles.uploadButton}
                      onPress={() => {
                        setSelectedCourseIds([...courseFilterDraft]);
                        setShowCourseFilterModal(false);
                      }}
                    >
                      <Text style={styles.uploadButtonText}>Apply</Text>
                    </TouchableOpacity>
                  </View>
                </View>
              </View>
            </Modal>
          </View>
        )}
      </ScrollView>
      
      {renderBreakModal()}
      <Stopwatch ref={stopwatchRef} />
    </View>
  );
}

const getStyles = (theme) => StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: theme.isDarkMode ? theme.colors.background : '#FFFFFF',
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingTop: 50,
    paddingBottom: 20,
    paddingHorizontal: 20,
    backgroundColor: 'transparent',
  },
  hamburgerContainer: {
    marginRight: 15,
  },
  breadcrumbContainer: {
    flex: 1,
  },
  breadcrumb: {
    fontSize: 18,
    fontWeight: '600',
    color: theme.colors.heading,
  },
  profileIconContainer: {
    backgroundColor: 'transparent',
  },
  tabsContainer: {
    flexDirection: 'row',
    borderBottomWidth: 1,
    borderBottomColor: theme.colors.border,
    paddingHorizontal: 20,
  },
  tab: {
    flex: 1,
    paddingVertical: 15,
    alignItems: 'center',
    borderBottomWidth: 2,
    borderBottomColor: 'transparent',
  },
  activeTab: {
    borderBottomColor: theme.colors.primary || '#d4af37',
  },
  tabText: {
    fontSize: 14,
    color: theme.colors.text,
    opacity: 0.6,
  },
  activeTabText: {
    opacity: 1,
    fontWeight: '600',
    color: theme.colors.heading,
  },
  content: {
    flex: 1,
    paddingHorizontal: 20,
  },
  tabContent: {
    paddingVertical: 20,
  },
  sectionTitle: {
    fontSize: 20,
    fontWeight: 'bold',
    color: theme.colors.heading,
    marginBottom: 20,
  },
  subsectionTitle: {
    fontSize: 16,
    fontWeight: '600',
    color: theme.colors.heading,
    marginTop: 4,
    marginBottom: 10,
  },
  sectionPill: {
    alignSelf: 'flex-start',
    paddingVertical: 10,
    paddingHorizontal: 16,
    borderRadius: 22,
    borderWidth: 2,
    backgroundColor: 'transparent',
    marginBottom: 12,
  },
  sectionPillText: {
    fontSize: 16,
    fontWeight: '600',
  },
  filterCoursesButton: {
    alignSelf: 'flex-start',
    paddingVertical: 12,
    paddingHorizontal: 18,
    borderRadius: 22,
    borderWidth: 2,
    backgroundColor: 'transparent',
    marginBottom: 8,
  },
  filterCoursesButtonText: {
    fontSize: 15,
    fontWeight: '600',
  },
  clearFilterLink: {
    marginBottom: 16,
  },
  courseBreaksListOuter: {
    marginBottom: 24,
  },
  breakRowTitleRow: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    marginBottom: 5,
    backgroundColor: 'transparent',
  },
  breakNameInRow: {
    flex: 1,
    marginBottom: 0,
    paddingRight: 10,
    minWidth: 0,
    backgroundColor: 'transparent',
  },
  breakRowCourseTag: {
    fontSize: 14,
    fontWeight: '600',
    color: theme.colors.heading,
    maxWidth: '52%',
    textAlign: 'right',
    flexShrink: 0,
    backgroundColor: 'transparent',
  },
  clearFilterText: {
    fontSize: 14,
    color: theme.colors.primary || '#d4af37',
    textDecorationLine: 'underline',
  },
  courseFilterScroll: {
    maxHeight: 320,
    marginBottom: 12,
  },
  modalHint: {
    fontSize: 14,
    color: theme.colors.text,
    opacity: 0.8,
    marginBottom: 16,
    lineHeight: 20,
  },
  checkboxRow: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 12,
    borderBottomWidth: 1,
    borderBottomColor: theme.colors.border,
  },
  checkboxMark: {
    fontSize: 18,
    marginRight: 12,
    color: theme.colors.text,
  },
  modalActionsRow: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    marginTop: 8,
  },
  modalSecondaryButton: {
    paddingVertical: 12,
    paddingHorizontal: 18,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: theme.colors.border,
    marginRight: 12,
  },
  modalSecondaryButtonText: {
    fontSize: 15,
    color: theme.colors.text,
    fontWeight: '600',
  },
  section: {
    marginBottom: 30,
  },
  sectionHeader: {
    fontSize: 16,
    fontWeight: '600',
    color: theme.colors.text,
    marginBottom: 10,
  },
  breakRow: {
    padding: 15,
    borderBottomWidth: 1,
    borderBottomColor: theme.colors.border,
    backgroundColor: theme.isDarkMode ? theme.colors.surface : '#F9F9F9',
    marginBottom: 5,
    borderRadius: 8,
  },
  breakName: {
    fontSize: 16,
    fontWeight: '600',
    color: theme.colors.text,
    marginBottom: 5,
  },
  breakDetails: {
    fontSize: 14,
    color: theme.colors.text,
    opacity: 0.7,
    marginBottom: 8,
  },
  statusBadge: {
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 4,
    alignSelf: 'flex-start',
  },
  statusBadgeText: {
    fontSize: 12,
    fontWeight: '600',
    color: '#FFFFFF',
    backgroundColor: 'transparent',
  },
  statuscompleted: {
    backgroundColor: '#4CAF50',
  },
  statusinprogress: {
    backgroundColor: '#FF9800',
  },
  statusattempted: {
    backgroundColor: '#F44336',
  },
  statusnotstarted: {
    backgroundColor: '#9E9E9E',
  },
  loadingText: {
    fontSize: 14,
    color: theme.colors.text,
    opacity: 0.6,
    textAlign: 'center',
    padding: 20,
  },
  emptyText: {
    fontSize: 14,
    color: theme.colors.text,
    opacity: 0.5,
    textAlign: 'center',
    padding: 20,
  },
  dropdownContainer: {
    marginBottom: 20,
    zIndex: 1000,
  },
  dropdownButton: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    backgroundColor: theme.isDarkMode ? theme.colors.surface : '#F5F5F5',
    padding: 15,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: theme.colors.border,
  },
  dropdownButtonText: {
    fontSize: 16,
    color: theme.colors.text,
    flex: 1,
    backgroundColor: 'transparent',
  },
  dropdownArrow: {
    fontSize: 12,
    color: theme.colors.text,
    backgroundColor: 'transparent',
  },
  dropdownMenu: {
    position: 'absolute',
    top: '100%',
    left: 0,
    right: 0,
    backgroundColor: theme.isDarkMode ? theme.colors.surface : '#FFFFFF',
    borderRadius: 8,
    borderWidth: 1,
    borderColor: theme.colors.border,
    marginTop: 5,
    maxHeight: 200,
    zIndex: 1001,
  },
  dropdownScrollView: {
    maxHeight: 200,
  },
  dropdownOption: {
    padding: 15,
    borderBottomWidth: 1,
    borderBottomColor: theme.colors.border,
    backgroundColor: 'transparent',
  },
  dropdownOptionText: {
    fontSize: 16,
    color: theme.colors.text,
    backgroundColor: 'transparent',
  },
  toggleButton: {
    backgroundColor: theme.colors.primary || '#d4af37',
    padding: 15,
    borderRadius: 8,
    marginBottom: 10,
    alignItems: 'center',
  },
  toggleButtonText: {
    color: '#FFFFFF',
    fontSize: 16,
    fontWeight: '600',
  },
  tableContainer: {
    marginBottom: 20,
    backgroundColor: theme.isDarkMode ? theme.colors.surface : '#F9F9F9',
    borderRadius: 8,
    padding: 10,
  },
  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'center',
    alignItems: 'center',
  },
  modalContent: {
    width: '90%',
    maxWidth: 600,
    backgroundColor: theme.isDarkMode ? theme.colors.surface : '#FFFFFF',
    borderRadius: 12,
    padding: 20,
    ...(Platform.OS === 'web' ? {
      boxShadow: '0 4px 20px rgba(0, 0, 0, 0.3)',
    } : {
      shadowColor: '#000',
      shadowOffset: { width: 0, height: 4 },
      shadowOpacity: 0.3,
      shadowRadius: 8,
      elevation: 10,
    }),
  },
  modalCloseButton: {
    position: 'absolute',
    top: 10,
    right: 10,
    width: 32,
    height: 32,
    borderRadius: 16,
    backgroundColor: theme.isDarkMode ? 'rgba(255, 255, 255, 0.2)' : 'rgba(0, 0, 0, 0.1)',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 10,
  },
  modalCloseText: {
    color: theme.colors.text,
    fontSize: 20,
    fontWeight: 'bold',
  },
  modalTitle: {
    fontSize: 20,
    fontWeight: 'bold',
    color: theme.colors.heading,
    marginBottom: 15,
    textAlign: 'center',
  },
  youtubeContainer: {
    width: '100%',
    aspectRatio: 16 / 9,
    borderRadius: 8,
    overflow: 'hidden',
    backgroundColor: '#000',
    marginBottom: 15,
  },
  youtubeIframe: {
    width: '100%',
    height: '100%',
  },
  youtubeLinkText: {
    color: theme.colors.text,
    fontSize: 14,
    textAlign: 'center',
    marginBottom: 10,
    padding: 20,
  },
  youtubeNoteText: {
    color: theme.colors.text,
    fontSize: 12,
    textAlign: 'center',
    fontStyle: 'italic',
    opacity: 0.7,
  },
  videoTitle: {
    fontSize: 16,
    fontWeight: '600',
    color: theme.colors.text,
    textAlign: 'center',
    marginTop: 10,
  },
  noVideoContainer: {
    alignItems: 'center',
    padding: 30,
  },
  noVideoText: {
    fontSize: 16,
    color: theme.colors.text,
    marginBottom: 20,
    textAlign: 'center',
  },
  uploadButton: {
    backgroundColor: theme.colors.primary || '#d4af37',
    paddingHorizontal: 30,
    paddingVertical: 12,
    borderRadius: 8,
  },
  uploadButtonText: {
    color: '#000000',
    fontSize: 16,
    fontWeight: '600',
  },
});
